import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { INSTRUCTOR_PERMISSIONS } from '../../../routes/instructorRoutes';
import { updateUserPassword } from '../../../services/adminAuthService';

// Définition de toutes les permissions disponibles avec leurs descriptions
const AVAILABLE_PERMISSIONS = {
  'view_dashboard': {
    label: 'Tableau de bord',
    description: 'Accès au tableau de bord instructeur',
    category: 'Navigation'
  },
  'view_courses': {
    label: 'Voir les cours',
    description: 'Consulter la liste des cours',
    category: 'Cours'
  },
  'create_courses': {
    label: 'Créer des cours',
    description: 'Créer de nouveaux cours',
    category: 'Cours'
  },
  'edit_own_courses': {
    label: 'Modifier ses cours',
    description: 'Modifier ses propres cours',
    category: 'Cours'
  },
  'view_students': {
    label: 'Voir les étudiants',
    description: 'Consulter la liste des étudiants',
    category: 'Étudiants'
  },
  'view_student_progress': {
    label: 'Suivi des étudiants',
    description: 'Voir les progrès des étudiants',
    category: 'Étudiants'
  },
  'view_analytics': {
    label: 'Analytics',
    description: 'Accès aux statistiques et analyses',
    category: 'Analytics'
  },
  'create_assessments': {
    label: 'Créer des évaluations',
    description: 'Créer des quiz et évaluations',
    category: 'Évaluations'
  },
  'grade_assessments': {
    label: 'Noter les évaluations',
    description: 'Corriger et noter les évaluations',
    category: 'Évaluations'
  },
  'schedule_live_sessions': {
    label: 'Sessions live',
    description: 'Programmer des sessions en direct',
    category: 'Sessions'
  },
  'send_messages': {
    label: 'Messagerie',
    description: 'Envoyer des messages aux étudiants',
    category: 'Communication'
  }
} as const;
import { 
  Users, 
  Search, 
  Plus, 
  MoreHorizontal, 
  Edit, 
  Trash, 
  ChevronDown, 
  ChevronUp, 
  ExternalLink, 
  Mail, 
  Phone, 
  Globe, 
  BookOpen,
  Save,
  X,
  Eye,
  EyeOff,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Image,
  Upload
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import { 
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { MediaSelector } from '../../../components/media/MediaSelector';

interface Instructor {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  avatar_url: string | null;
  bio: string | null;
  website: string | null;
  phone: string | null;
  expertise: string[] | null;
  social_links: Record<string, any>;
  is_active: boolean;
  course_count: number;
  created_at: string;
  updated_at: string;
  // Données utilisateur associées
  user?: {
    id: string;
    email: string;
    role: string;
    is_active: boolean;
  };
  // Permissions
  permissions?: string[];
}

const AdminInstructorsPage: React.FC = () => {
  useCheckPermissions(['admin']);
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [instructorsPerPage] = useState(10);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState<Instructor | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    avatar_url: '',
    bio: '',
    website: '',
    phone: '',
    expertise: [] as string[],
    social_links: {} as Record<string, any>,
    is_active: true
  });
  
  // États pour la gestion des mots de passe
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  // États pour la gestion des permissions
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [permissionsInstructor, setPermissionsInstructor] = useState<Instructor | null>(null);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const uploadFileAndGetUrl = async (file: File): Promise<string> => {
    const { tenantId } = getDomainFilterParams();
    const fileExt = file.name.split('.').pop();
    const fileName = `instructor_avatar_${uuidv4()}.${fileExt}`;
    const filePath = `tenant/${tenantId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data: publicUrlData } = supabase.storage
      .from('media')
      .getPublicUrl(filePath);

    return publicUrlData.publicUrl;
  };

  useEffect(() => {
    const { tenantId } = getDomainFilterParams();
    if (tenantId) {
      fetchInstructors();
    }
  }, [sortBy, sortOrder]);

  const fetchInstructors = async () => {
    const { domain, tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    setLoading(true);
    try {
      // Récupérer les instructeurs (ne pas trier côté DB si on trie par course_count)
      let instructorsQuery = supabase
        .from('elearn_instructors')
        .select('*');
      
      instructorsQuery = applyDomainFilterToQuery(instructorsQuery);

      if (sortBy !== 'course_count') {
        instructorsQuery = instructorsQuery.order(sortBy, { ascending: sortOrder === 'asc' });
      }

      const { data: instructorsData, error: instructorsError } = await instructorsQuery;
      if (instructorsError) throw instructorsError;

      // Récupérer les permissions et données utilisateur pour chaque instructeur
      const instructorsWithPermissions = await Promise.all(
        (instructorsData || []).map(async (instructor) => {
          // Récupérer les permissions
          const { data: permissions } = await supabase
            .from('elearn_instructor_permissions')
            .select('permission_type')
            .eq('instructor_id', instructor.id)
            .eq('is_granted', true);
          
          // Récupérer les données utilisateur si user_id existe
          let userData = null;
          if (instructor.user_id) {
            const { data: user } = await supabase
              .from('users')
              .select('id, email, role, is_active')
              .eq('id', instructor.user_id)
              .single();
            userData = user;
          }
          
          return {
            ...instructor,
            user: userData,
            permissions: permissions?.map(p => p.permission_type) || [],
            course_count: 0,
          } as Instructor;
        })
      );

      // Récupérer tous les cours (id, instructor_id) pour calculer les compteurs par instructeur
      let coursesQuery = supabase
        .from('elearn_courses')
        .select('id, instructor_id');
      
      coursesQuery = applyDomainFilterToQuery(coursesQuery);
      const { data: coursesData, error: coursesError } = await coursesQuery;
      if (coursesError) throw coursesError;

      const countsMap = new Map<string, number>();
      (coursesData || []).forEach((c: { id: string; instructor_id: string | null }) => {
        if (c.instructor_id) {
          countsMap.set(c.instructor_id, (countsMap.get(c.instructor_id) || 0) + 1);
        }
      });

      const withCounts = instructorsWithPermissions.map((inst) => ({
        ...inst,
        course_count: countsMap.get(inst.id) || 0,
      }));

      // Tri côté client si nécessaire (incluant course_count)
      const sorted = [...withCounts].sort((a, b) => {
        const dir = sortOrder === 'asc' ? 1 : -1;
        if (sortBy === 'course_count') {
          return (a.course_count - b.course_count) * dir;
        }
        if (sortBy === 'name') {
          return a.name.localeCompare(b.name) * dir;
        }
        if (sortBy === 'created_at') {
          return ((new Date(a.created_at).getTime()) - (new Date(b.created_at).getTime())) * dir;
        }
        // Fallback: ne pas modifier l'ordre
        return 0;
      });

      setInstructors(sorted);
    } catch (error) {
      console.error('Erreur lors de la récupération des instructeurs:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les instructeurs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour gérer les permissions instructeur
  const handlePermissionsClick = async (instructor: Instructor) => {
    try {
      // Récupérer les permissions actuelles
      const { data: permissions, error } = await supabase
        .from('elearn_instructor_permissions')
        .select('permission_type')
        .eq('instructor_id', instructor.id)
        .eq('is_granted', true);
      
      if (error) throw error;
      
      setPermissionsInstructor(instructor);
      setSelectedPermissions(permissions?.map(p => p.permission_type) || []);
      setShowPermissionsDialog(true);
    } catch (error) {
      console.error('Erreur lors du chargement des permissions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les permissions',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour sauvegarder les permissions
  const handleSavePermissions = async () => {
    if (!permissionsInstructor) return;
    
    // Récupérer les paramètres de filtrage par domaine
    const { domain, tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    try {
      // Supprimer toutes les permissions existantes
      await supabase
        .from('elearn_instructor_permissions')
        .delete()
        .eq('instructor_id', permissionsInstructor.id);
      
      // Ajouter les nouvelles permissions
      if (selectedPermissions.length > 0) {
        const permissionsToInsert = selectedPermissions.map(permission => ({
          instructor_id: permissionsInstructor.id,
          permission_type: permission,
          is_granted: true,
          tenant_id: tenantId,
          domain: domain
        }));
        
        const { error } = await supabase
          .from('elearn_instructor_permissions')
          .insert(permissionsToInsert);
        
        if (error) throw error;
      }
      
      toast({
        title: 'Succès',
        description: 'Permissions mises à jour avec succès',
      });
      
      setShowPermissionsDialog(false);
      fetchInstructors(); // Recharger la liste
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des permissions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder les permissions',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour activer/désactiver un instructeur
  const handleToggleInstructorStatus = async (instructor: Instructor) => {
    try {
      const { error } = await supabase
        .from('elearn_instructors')
        .update({ is_active: !instructor.is_active })
        .eq('id', instructor.id);
      
      if (error) throw error;
      
      // Mettre à jour le rôle utilisateur aussi
      if (instructor.user_id) {
        await supabase
          .from('users')
          .update({ is_active: !instructor.is_active })
          .eq('id', instructor.user_id);
      }
      
      toast({
        title: 'Succès',
        description: `Instructeur ${!instructor.is_active ? 'activé' : 'désactivé'} avec succès`,
      });
      
      fetchInstructors(); // Recharger la liste
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le statut de l\'instructeur',
        variant: 'destructive',
      });
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddInstructor = () => {
    setEditingInstructor(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      avatar_url: '',
      bio: '',
      website: '',
      phone: '',
      expertise: [],
      social_links: {},
      is_active: true
    });
    setPasswordError('');
    setIsDialogOpen(true);
  };

  const handleEditInstructor = (instructor: Instructor) => {
    setEditingInstructor(instructor);
    setFormData({
      name: instructor.name,
      email: instructor.email,
      password: '', // Vide pour modification
      confirmPassword: '',
      avatar_url: instructor.avatar_url || '',
      bio: instructor.bio || '',
      website: instructor.website || '',
      phone: instructor.phone || '',
      expertise: instructor.expertise || [],
      social_links: instructor.social_links || {},
      is_active: instructor.is_active
    });
    setPasswordError('');
    setIsDialogOpen(true);
  };

  // Fonction de validation des mots de passe
  const validatePassword = (): boolean => {
    // Pour la création, le mot de passe est obligatoire
    if (!editingInstructor && !formData.password) {
      setPasswordError('Le mot de passe est requis pour créer un nouvel instructeur');
      return false;
    }
    
    // Si un mot de passe est fourni, valider
    if (formData.password) {
      if (formData.password.length < 6) {
        setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
        return false;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setPasswordError('Les mots de passe ne correspondent pas');
        return false;
      }
    }
    
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { domain, tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    // Vérifier que l'utilisateur est toujours authentifié
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    console.log('=== VÉRIFICATION SESSION ===');
    console.log('Session Error:', sessionError);
    console.log('Session:', session);
    console.log('User ID:', session?.user?.id);
    console.log('User Email:', session?.user?.email);
    console.log('Access Token présent:', !!session?.access_token);
    
    if (sessionError || !session) {
      console.error('❌ Session invalide ou expirée');
      toast({
        title: 'Session expirée',
        description: 'Votre session a expiré. Veuillez vous reconnecter.',
        variant: 'destructive',
      });
      navigate('/admin/login');
      return;
    }
    
    console.log('✅ Session valide, tentative de création instructeur...');
    
    // Validation simple
    if (!formData.name || !formData.email) {
      toast({
        title: 'Erreur de validation',
        description: 'Le nom et l\'email sont requis.',
        variant: 'destructive',
      });
      return;
    }
    
    // Validation du mot de passe
    if (!validatePassword()) {
      return;
    }
    
    try {
      if (editingInstructor) {
        // Mise à jour d'un instructeur existant
        
        // 1. Mettre à jour le compte utilisateur si nécessaire
        if (editingInstructor.user_id) {
          const userUpdates: any = {
            name: formData.name,
            email: formData.email,
            is_active: formData.is_active
          };
          
          // Mettre à jour le mot de passe si fourni (via Edge Function)
          if (formData.password && formData.password.trim()) {
            console.log('🔐 Mise à jour du mot de passe via Edge Function...');
            
            const passwordResult = await updateUserPassword({
              userId: editingInstructor.user_id,
              password: formData.password,
              email: formData.email,
              name: formData.name
            });

            if (!passwordResult.success) {
              console.error('Erreur lors de la mise à jour du mot de passe:', passwordResult.error);
              toast({
                title: 'Erreur',
                description: passwordResult.error || 'Impossible de mettre à jour le mot de passe.',
                variant: 'destructive',
              });
              throw new Error(passwordResult.error);
            }
            
            console.log('✅ Mot de passe mis à jour avec succès');
          }
          
          // Mettre à jour les autres données utilisateur
          const { error: userError } = await supabase
            .from('users')
            .update(userUpdates)
            .eq('id', editingInstructor.user_id);
          
          if (userError) throw userError;
        }
        
        // Mettre à jour le profil instructeur
        const { error } = await supabase
          .from('elearn_instructors')
          .update({
            name: formData.name,
            email: formData.email,
            avatar_url: formData.avatar_url || null,
            bio: formData.bio || null,
            website: formData.website || null,
            phone: formData.phone || null,
            expertise: formData.expertise,
            social_links: formData.social_links,
            is_active: formData.is_active,
            domain: domain,
            tenant_id: tenantId,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingInstructor.id);

        if (error) throw error;
        
        // Mettre à jour l'état local
        const updatedInstructors = instructors.map(instructor => {
          if (instructor.id === editingInstructor.id) {
            return {
              ...instructor,
              ...formData,
            };
          }
          return instructor;
        });
        
        setInstructors(updatedInstructors);
        
        const passwordUpdated = formData.password && formData.password.trim();
        toast({
          title: 'Instructeur mis à jour',
          description: `${formData.name} a été mis à jour avec succès${passwordUpdated ? ' (mot de passe inclus)' : ''}.`,
        });
      } else {
        // Création d'un nouvel instructeur avec compte Supabase Auth
        // Sauvegarder la session admin actuelle
        const { data: { session: adminSession } } = await supabase.auth.getSession();
        
        console.log('💾 Sauvegarde de la session admin avant création utilisateur');
        
        // Créer le nouvel utilisateur (cela va temporairement changer la session)
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              name: formData.name,
              role: 'instructor',
              tenant_id: tenantId.toString(),
              domain: domain
            }
          }
        });

        if (authError) throw authError;

        // Restaurer immédiatement la session admin
        if (adminSession) {
          console.log('🔄 Restauration de la session admin');
          await supabase.auth.setSession({
            access_token: adminSession.access_token,
            refresh_token: adminSession.refresh_token
          });
        }

        if (authData.user) {
          // Le trigger on_auth_user_created a déjà créé l'entrée dans public.users
          // Nous devons juste mettre à jour is_active si nécessaire
          if (!formData.is_active) {
            const { error: userError } = await supabase
              .from('users')
              .update({ is_active: formData.is_active })
              .eq('id', authData.user.id);

            if (userError) throw userError;
          }

          // Créer le profil instructeur
          const newInstructorId = uuidv4();
          
          console.log('=== TENTATIVE INSERT INSTRUCTEUR ===');
          console.log('Instructor ID:', newInstructorId);
          console.log('User ID:', authData.user.id);
          console.log('Tenant ID:', tenantId);
          console.log('Domain:', domain);
          
          // Vérifier à nouveau la session juste avant l'insert
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          console.log('Session juste avant INSERT:', {
            hasSession: !!currentSession,
            userId: currentSession?.user?.id,
            hasAccessToken: !!currentSession?.access_token
          });
          
          const { error } = await supabase
            .from('elearn_instructors')
            .insert({
              id: newInstructorId,
              tenant_id: tenantId,
              domain: domain,
              user_id: authData.user.id,
              name: formData.name,
              email: formData.email,
              avatar_url: formData.avatar_url || null,
              bio: formData.bio || null,
              website: formData.website || null,
              phone: formData.phone || null,
              expertise: formData.expertise,
              social_links: formData.social_links,
              is_active: formData.is_active,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (error) throw error;

          console.log('✅ Instructeur créé avec succès !');
          console.log('ℹ️ Le trigger trigger_sync_instructor_role a automatiquement créé le mapping user-instructor');

          // Le mapping user-instructor est créé automatiquement par le trigger
          // trigger_sync_instructor_role sur elearn_instructors
          // Pas besoin de le créer manuellement

          // Mettre à jour l'état local
          const newInstructor: Instructor = {
            id: newInstructorId,
            tenant_id: tenantId.toString(),
            user_id: authData.user.id,
            name: formData.name,
            email: formData.email,
            avatar_url: formData.avatar_url || null,
            bio: formData.bio || null,
            website: formData.website || null,
            phone: formData.phone || null,
            expertise: formData.expertise,
            social_links: formData.social_links,
            is_active: formData.is_active,
            course_count: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user: undefined,
            permissions: ['view_dashboard', 'view_courses', 'view_students']
          };

          setInstructors([...instructors, newInstructor]);
          toast({
            title: 'Instructeur créé',
            description: `${formData.name} a été créé avec succès. Il peut maintenant se connecter.`,
          });
        }
      }
      
      // Réinitialiser le formulaire et fermer la boîte de dialogue
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        avatar_url: '',
        bio: '',
        website: '',
        phone: '',
        expertise: [],
        social_links: {},
        is_active: true
      });
      setEditingInstructor(null);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'enregistrement.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteInstructor = async (instructorId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet instructeur ?')) {
      return;
    }
    
    try {
      // Vérifier si l'instructeur a des cours associés
      const { count, error: countError } = await supabase
        .from('elearn_courses')
        .select('id', { count: 'exact', head: true })
        .eq('instructor_id', instructorId);

      if (countError) throw countError;

      if (count && count > 0) {
        toast({
          title: 'Suppression impossible',
          description: `Cet instructeur est associé à ${count} cours. Veuillez d'abord réaffecter ou supprimer ces cours.`,
          variant: 'destructive',
        });
        return;
      }

      // Supprimer l'instructeur de Supabase
      const { error } = await supabase
        .from('elearn_instructors')
        .delete()
        .eq('id', instructorId);

      if (error) throw error;
      
      // Mettre à jour l'état local
      const updatedInstructors = instructors.filter(instructor => instructor.id !== instructorId);
      setInstructors(updatedInstructors);
      
      toast({
        title: 'Instructeur supprimé',
        description: 'L\'instructeur a été supprimé avec succès.',
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression.',
        variant: 'destructive',
      });
    }
  };

  const filteredInstructors = instructors.filter(instructor => 
    instructor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instructor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instructor.bio?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Réinitialiser la page courante lorsque les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);
  
  // Calculer les indices des instructeurs à afficher sur la page courante
  const indexOfLastInstructor = currentPage * instructorsPerPage;
  const indexOfFirstInstructor = indexOfLastInstructor - instructorsPerPage;
  const currentInstructors = filteredInstructors.slice(indexOfFirstInstructor, indexOfLastInstructor);
  
  // Fonction pour changer de page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(filteredInstructors.length / instructorsPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Users className="h-8 w-8 text-[#D4AF37]" />
              Instructeurs
            </h1>
            <p className="text-gray-400 mt-2">
              {filteredInstructors.length} instructeur{filteredInstructors.length > 1 ? 's' : ''} trouvé{filteredInstructors.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={handleAddInstructor}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
          >
            <Plus className="mr-2" size={18} />
            Nouvel instructeur
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{instructors.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total cours</div>
            <div className="text-2xl font-bold text-blue-400">
              {instructors.reduce((sum, inst) => sum + inst.course_count, 0)}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Moyenne par instructeur</div>
            <div className="text-2xl font-bold text-[#D4AF37]">
              {instructors.length > 0 
                ? Math.round(instructors.reduce((sum, inst) => sum + inst.course_count, 0) / instructors.length * 10) / 10
                : 0}
            </div>
          </div>
        </div>

        {/* Recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Rechercher un instructeur..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
            />
          </div>
        </div>

        {/* Table des instructeurs */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : filteredInstructors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucun instructeur trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Instructeur
                        <ArrowUpDown size={14} className={sortBy === 'name' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Bio
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('course_count')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Cours
                        <ArrowUpDown size={14} className={sortBy === 'course_count' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Date ajout
                        <ArrowUpDown size={14} className={sortBy === 'created_at' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {currentInstructors.map((instructor, index) => (
                    <tr
                      key={instructor.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={instructor.avatar_url || ''} alt={instructor.name} />
                            <AvatarFallback className="bg-[#D4AF37]/10 text-[#D4AF37]">
                              {getInitials(instructor.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{instructor.name}</div>
                            <div className="text-sm text-gray-400">
                              {instructor.is_active ? (
                                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Actif</Badge>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Inactif</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{instructor.email}</div>
                        {instructor.phone && (
                          <div className="text-sm text-gray-400">{instructor.phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white max-w-xs truncate">
                          {instructor.bio || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          {instructor.course_count}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePermissionsClick(instructor)}
                          className="text-[#D4AF37] hover:text-[#B8941F]"
                        >
                          {instructor.permissions?.length || 0} permissions
                        </Button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{formatDate(instructor.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-white">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-gray-900 text-white border-white/10">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10"
                              onClick={() => handleEditInstructor(instructor)}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              <span>Modifier</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10"
                              onClick={() => handlePermissionsClick(instructor)}
                            >
                              <Users className="mr-2 h-4 w-4" />
                              <span>Permissions</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10"
                              onClick={() => navigate(`/admin/elearning/courses?instructor=${instructor.id}`)}
                            >
                              <BookOpen className="mr-2 h-4 w-4" />
                              <span>Voir les cours</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer text-red-500 hover:bg-white/10 hover:text-red-500"
                              onClick={() => handleDeleteInstructor(instructor.id)}
                            >
                              <Trash className="mr-2 h-4 w-4" />
                              <span>Supprimer</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Contrôles de pagination */}
          {filteredInstructors.length > 0 && (
              <div className="flex justify-between items-center p-4 border-t border-white/10">
                <div className="text-sm text-white/70">
                  Instructeurs ({filteredInstructors.length}) - Page {currentPage}/{totalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Afficher les pages autour de la page courante
                    let pageToShow;
                    if (totalPages <= 5) {
                      // Si moins de 5 pages, afficher toutes les pages
                      pageToShow = i + 1;
                    } else if (currentPage <= 3) {
                      // Si on est au début, afficher les 5 premières pages
                      pageToShow = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Si on est à la fin, afficher les 5 dernières pages
                      pageToShow = totalPages - 4 + i;
                    } else {
                      // Sinon, afficher 2 pages avant et 2 pages après la page courante
                      pageToShow = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageToShow}
                        variant={currentPage === pageToShow ? "default" : "outline"}
                        size="sm"
                        onClick={() => paginate(pageToShow)}
                        className={currentPage === pageToShow 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "border-white/10 text-white hover:bg-white/5"}
                      >
                        {pageToShow}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
          )}
        </div>
      </div>

      {/* Dialog pour ajouter/modifier un instructeur */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-white/10">
          <DialogHeader>
            <DialogTitle>{editingInstructor ? 'Modifier l\'instructeur' : 'Nouvel instructeur'}</DialogTitle>
            <DialogDescription>
              {editingInstructor 
                ? 'Modifiez les informations de l\'instructeur ci-dessous.' 
                : 'Remplissez les informations pour créer un nouvel instructeur.'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nom de l'instructeur"
                className="bg-black/20 border-white/10 text-white"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@exemple.com"
                className="bg-black/20 border-white/10 text-white"
                required
              />
            </div>
            
            {/* Champs de mot de passe */}
            <div className="space-y-2">
              <Label htmlFor="password">
                {editingInstructor ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={editingInstructor ? 'Laisser vide pour ne pas changer' : 'Mot de passe'}
                  className="bg-black/20 border-white/10 text-white pr-10"
                  required={!editingInstructor}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-white/70" />
                  ) : (
                    <Eye className="h-4 w-4 text-white/70" />
                  )}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirmer le mot de passe"
                  className="bg-black/20 border-white/10 text-white pr-10"
                  required={!editingInstructor || formData.password !== ''}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-white/70" />
                  ) : (
                    <Eye className="h-4 w-4 text-white/70" />
                  )}
                </Button>
              </div>
              {passwordError && (
                <p className="text-sm text-red-400 mt-1">{passwordError}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="avatar_url">URL de l'avatar</Label>
              <div className="space-y-3">
                <Input
                  id="avatar_url"
                  name="avatar_url"
                  value={formData.avatar_url}
                  onChange={handleInputChange}
                  placeholder="https://exemple.com/avatar.jpg"
                  className="bg-black/20 border-white/10 text-white"
                />
                
                {formData.avatar_url && (
                  <div className="relative w-full h-32 bg-white/10 rounded-lg overflow-hidden">
                    <img
                      src={formData.avatar_url}
                      alt="Aperçu de l'avatar"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({...formData, avatar_url: ''})}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      // Fermer la modale d'édition d'instructeur pour éviter les problèmes de superposition
                      setIsDialogOpen(false);
                      setShowAvatarSelector(true);
                    }}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <Image className="mr-2 h-4 w-4" />
                    Médiathèque
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = async (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          setIsUploading(true);
                          try {
                            const url = await uploadFileAndGetUrl(file);
                            setFormData({...formData, avatar_url: url});
                            toast({
                              title: 'Avatar uploadé',
                              description: 'L\'avatar a été uploadé avec succès.',
                            });
                          } catch (error) {
                            console.error('Erreur lors de l\'upload de l\'avatar:', error);
                            toast({
                              title: 'Erreur d\'upload',
                              description: 'Impossible d\'uploader l\'avatar.',
                              variant: 'destructive',
                            });
                          } finally {
                            setIsUploading(false);
                          }
                        }
                      };
                      input.click();
                    }}
                    disabled={isUploading}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    {isUploading ? (
                      <>
                        <span className="mr-2 h-4 w-4">⏳</span>
                        Upload en cours...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Uploader
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+33612345678"
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website">Site web</Label>
              <Input
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://exemple.com"
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bio">Biographie</Label>
              <Textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                placeholder="Biographie de l'instructeur"
                className="bg-black/20 border-white/10 text-white"
                rows={3}
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-white/10 text-white hover:bg-white/10"
              >
                <X className="mr-2" size={16} />
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save className="mr-2" size={16} />
                {editingInstructor ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialogue de gestion des permissions */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Gérer les permissions - {permissionsInstructor?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Sélectionnez les permissions à accorder à cet enseignant. Les permissions sont organisées par catégorie.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Grouper les permissions par catégorie */}
            {Object.entries(
              Object.entries(AVAILABLE_PERMISSIONS).reduce((acc, [key, permission]) => {
                const category = permission.category;
                if (!acc[category]) acc[category] = [];
                acc[category].push({ key, ...permission });
                return acc;
              }, {} as Record<string, Array<{ key: string; label: string; description: string; category: string }>>)
            ).map(([category, permissions]) => (
              <Card key={category} className="bg-black/20 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    {category === 'Navigation' && <Users className="h-5 w-5 text-blue-400" />}
                    {category === 'Cours' && <BookOpen className="h-5 w-5 text-green-400" />}
                    {category === 'Étudiants' && <Users className="h-5 w-5 text-purple-400" />}
                    {category === 'Analytics' && <ArrowUpDown className="h-5 w-5 text-yellow-400" />}
                    {category === 'Évaluations' && <Edit className="h-5 w-5 text-red-400" />}
                    {category === 'Sessions' && <ExternalLink className="h-5 w-5 text-indigo-400" />}
                    {category === 'Communication' && <Mail className="h-5 w-5 text-cyan-400" />}
                    {category}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {permissions.map((permission) => (
                    <div key={permission.key} className="flex items-start space-x-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800/70 transition-colors">
                      <input
                        type="checkbox"
                        id={permission.key}
                        checked={selectedPermissions.includes(permission.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPermissions([...selectedPermissions, permission.key]);
                          } else {
                            setSelectedPermissions(selectedPermissions.filter(p => p !== permission.key));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={permission.key} 
                          className="text-white font-medium cursor-pointer hover:text-blue-300 transition-colors"
                        >
                          {permission.label}
                        </label>
                        <p className="text-gray-400 text-sm mt-1">{permission.description}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
            
            {/* Actions rapides */}
            <Card className="bg-black/20 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Sélectionner toutes les permissions de base
                    const basicPermissions = ['view_dashboard', 'view_courses', 'view_students'];
                    setSelectedPermissions(basicPermissions);
                  }}
                  className="border-blue-500/50 text-blue-300 hover:bg-blue-900/30"
                >
                  Permissions de base
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Sélectionner toutes les permissions de cours
                    const coursePermissions = ['view_courses', 'create_courses', 'edit_own_courses'];
                    const newPermissions = [...new Set([...selectedPermissions, ...coursePermissions])];
                    setSelectedPermissions(newPermissions);
                  }}
                  className="border-green-500/50 text-green-300 hover:bg-green-900/30"
                >
                  + Gestion des cours
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Sélectionner toutes les permissions
                    setSelectedPermissions(Object.keys(AVAILABLE_PERMISSIONS));
                  }}
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-900/30"
                >
                  Toutes les permissions
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedPermissions([])}
                  className="border-red-500/50 text-red-300 hover:bg-red-900/30"
                >
                  Tout désélectionner
                </Button>
              </CardContent>
            </Card>
            
            {/* Résumé des permissions sélectionnées */}
            {selectedPermissions.length > 0 && (
              <Card className="bg-blue-900/20 border-blue-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-blue-300 text-lg">
                    Permissions sélectionnées ({selectedPermissions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {selectedPermissions.map((permission) => (
                      <Badge 
                        key={permission} 
                        variant="secondary" 
                        className="bg-blue-600/20 text-blue-300 border-blue-500/30"
                      >
                        {AVAILABLE_PERMISSIONS[permission as keyof typeof AVAILABLE_PERMISSIONS]?.label || permission}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPermissionsDialog(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              <X className="mr-2 h-4 w-4" />
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSavePermissions}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="mr-2 h-4 w-4" />
              Sauvegarder les permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Sélecteur de média pour l'avatar */}
      <MediaSelector
        isOpen={showAvatarSelector}
        onClose={() => setShowAvatarSelector(false)}
        onSelect={(url) => {
          setFormData({...formData, avatar_url: url});
          setShowAvatarSelector(false);
          // Réouvrir la modale d'édition d'instructeur
          setIsDialogOpen(true);
        }}
        selectedUrl={formData.avatar_url || undefined}
        title="Sélectionner un avatar"
        acceptedTypes={['image/*']}
      />
    </AdminLayout>
  );
};

export default AdminInstructorsPage;
